<template>
<div>
    <CCol sm="12">
        <CCard>
            <CCardBody class="d-flex flex-column">
                <div class="d-flex mb-2 justify-content-end align-items-start">
                    <!-- Title -->
                    <span class="flex-grow-1" v-if="title"><b>{{ $t('staticInfoIntro') }}</b> {{title}}</span>
                    <!-- Language dropdown -->
                    <LanguageDropdown v-model="currentLanguage"></LanguageDropdown>
                </div>
                <!-- Slug URL (edit mode only) -->
                <div v-if="baseStaticInfo && slugUrl" role="group" class="form-group col-12 p-0">
                    <label class="">URL</label>
                    <CInput type="text" :value="'/' + slugUrl" disabled></CInput>
                    <small class="text-muted d-block mt-1">{{ $t('slugExplanation') }}</small>
                    <div class="mt-1">
                        <small class="text-muted">{{ $t('urlPreview') }} </small>
                        <a :href="fullSlugUrl" target="_blank" rel="noopener noreferrer" style="font-size: 0.85rem; word-break: break-all;">{{ fullSlugUrl }}</a>
                    </div>
                </div>
                <div class="d-flex flex-column">
                    <!-- Image -->
                    <div ckass="col-12">
                        <GalleryPicker class="gallery-picker" v-model="staticInfo.image" :ratioWidth="16" :ratioHeight="9" v-bind:class="{'is-invalid': errorValidator('Image') === false }" :max="1"></GalleryPicker>
                    </div>
                    <!-- Titles -->
                    <div role="group" class="form-group col-12 p-0">
                        <label class=""> {{ $t('titleMandatory') }}</label>
                        <CInput type="text" v-model="staticInfo.titles[currentLanguage.key]" :invalid-feedback="errorOutput['Titles']" :is-valid="genericValidator(staticInfo.titles) && errorValidator('Titles')"></CInput>
                    </div>
                    <!-- Tabs -->
                    <div class="d-flex align-items-center col-12 p-0">
                        <label> {{ $t('tabs') }}</label>
                        <button type="button" class="btn mx-2 btn-add-tab" @click="addTab">{{ $t('addTab') }}</button>
                        <button type="button" class="btn btn-remove-tab" @click="removeCurrentTab">{{ $t('removeTab') }}</button>
                        <!-- Language dropdown -->
                        <div class="d-flex flex-grow-1 justify-content-end">
                            <LanguageDropdown class="align-self-end" v-model="currentLanguage"></LanguageDropdown>
                        </div>
                    </div>
                    <CTabs class="col-12 p-0 mt-3" v-if="staticInfo.tabs && staticInfo.tabs.length" variant="pills" @update:activeTab="tabChanged">
                        <CTab v-for="(tab, index) in staticInfo.tabs" :key="index" :title="$t('tab') + ' ' + (index + 1)">
                            <div class="d-flex flex-column mt-2">
                                <!-- Title -->
                                <div role="group" class="form-group col-12 mb-0">
                                    <label class=""> {{ $t('titleMandatory') }}</label>
                                    <CInput type="text" v-model="tab.titles[currentLanguage.key]" :invalid-feedback="errorOutput.Tabs[index].Titles" :is-valid="genericValidator(tab.titles) && errorValidator.Tabs[index].Titles"></CInput>
                                </div>
                                <!-- Contents -->
                                <div role="group" class="form-group col-12 mb-0">
                                    <label class=""> {{ $t('content') }}</label>
                                    <vue-editor v-model="tab.contents[currentLanguage.key]"></vue-editor>
                                </div>
                            </div>
                        </CTab>
                    </CTabs>
                </div>
                <!-- Documents -->
                <div class="col-12 p-0 mt-4">
                    <div class="d-flex align-items-center mb-2">
                        <label class="mb-0"><b>{{ $t('documents') || 'Documentos' }}</b></label>
                        <button type="button" class="btn mx-2 btn-add-tab" @click="addDocument">{{ $t('addDocument') || 'Adicionar documento' }}</button>
                    </div>
                    <input type="file" ref="documentInput" accept=".pdf" style="display:none" @change="onDocumentSelected" />
                    <div v-if="staticInfo.documents && staticInfo.documents.length">
                        <div v-for="(doc, index) in staticInfo.documents" :key="'doc-' + index" class="d-flex align-items-center mb-2 p-2 border rounded">
                            <div class="flex-grow-1">
                                <div class="d-flex align-items-center mb-1">
                                    <span class="badge badge-info mr-2">PDF</span>
                                    <small class="text-muted">{{ doc.originalFileName || doc.fileName || 'document.pdf' }}</small>
                                    <a v-if="doc.previewUrl" :href="doc.previewUrl" target="_blank" class="btn btn-sm btn-outline-primary ml-2">{{ $t('preview') || 'Pré-visualizar' }}</a>
                                </div>
                                <CInput type="text" :placeholder="($t('documentTitle') || 'Título do documento') + ' (' + currentLanguage.label + ')'" v-model="doc.titles[currentLanguage.key]" size="sm"></CInput>
                            </div>
                            <button type="button" class="btn btn-sm btn-danger ml-2" @click="removeDocument(index)">
                                <i class="cil-trash"></i> {{ $t('remove') || 'Remover' }}
                            </button>
                        </div>
                    </div>
                    <p v-else class="text-muted small">{{ $t('noDocuments') || 'Sem documentos anexados.' }}</p>
                </div>
                <!-- Action button + submitting -->
                <CSpinner v-if="submitting" variant="success" small class="align-self-end mt-3"></CSpinner>
                <CButton v-else type="button" class="btn btn-primary col-2 align-self-end mt-3" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
                <!-- Loading -->
                <FormLoading v-if="loading"></FormLoading>
            </CCardBody>
        </CCard>
    </CCol>
    <ConfirmationPopUp @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
    <ChoicePopUp
        :message="$t('pageCreatedAddToMenu')"
        :confirmText="$t('yesAddToMenu')"
        :cancelText="$t('noThanks')"
        @choiceConfirm="onConfirmAddToMenu"
        @choiceCancel="onCancelAddToMenu"
        v-model="showChoicePopup">
    </ChoicePopUp>
</div>
</template>
 
<script>
import { API_HELPER } from "@/helpers/api.js";
import { genericValidator, errorValidator, deepClone } from "@/helpers/utils.js";
import StaticInfo from "@/classes/StaticInfo";
import StaticInfoTab from "@/classes/StaticInfoTab";
import { mapState, mapActions } from 'vuex'
import LanguageDropdown from '@/controls/LanguageDropdown.vue'
import GalleryPicker from '@/controls/GalleryPicker.vue'
import { CONSTANTS } from '@/helpers/consts';
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import ChoicePopUp from "@/controls/popups/ChoicePopUp.vue";
import router from '@/router/index';
import { VueEditor } from "vue2-editor";
import FormLoading from '@/controls/FormLoading.vue'

const MAX_DOCUMENT_SIZE = 10; // MB

  export default {
  components: { LanguageDropdown, ConfirmationPopUp, ChoicePopUp, GalleryPicker, VueEditor, FormLoading },
    props:{    
        baseStaticInfo:
        {
            type: Object,
            default: null,
        },
        baseStaticInfoTitle:
        {
            type: String,
            default: null,
        },
        loading:
        {
            type: Boolean,
            default: false,
        },
    },
    data()
    {
        return {
            triedSubmit: false,
            currentLanguage: CONSTANTS.supportedLanguages[0],
            staticInfo: new StaticInfo({}, [], null, []),
            currentTab: 0,
            submitting: false,
            blobUrls: [],
            showConfirmationPopup: false,
            showChoicePopup: false,
        }
    },
    mounted: function()
    {
        this.resetStaticInfo();
    },
    computed:
    {
        ...mapState({ 
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        errorOutput: function()
        {
            var result = [];

            if(this.alertType == 'alert-danger' && this.alertOutput && Array.isArray(this.alertOutput))
            {
                result = this.alertOutput.reduce(function(accum, currentVal) {
                accum[currentVal.key] = currentVal.value;
                    return accum;
                }, {});
            }

            // Validate Title
            for (const [key, value] of Object.entries(this.staticInfo.titles)) {
                if(!value)
                {
                    result['Titles'] = this.$i18n.t("validateBothTranslations");
                    break;
                };
            }

            // Validate Title and Content on Tabs
            result.Tabs = [];
            const self = this;

            this.staticInfo.tabs.forEach(function(tab){
                let error = {};

                for (const [key, value] of Object.entries(tab.titles)) {
                    if(!value)
                    {
                        error['Titles'] = self.$i18n.t("validateBothTranslations");
                        break;
                    };
                }

                result.Tabs.push(error);
            });
            // console.log(result)
            return result;
        },
        ...mapState('staticInfos', ['postStaticInfoOutput']),
        ...mapState('staticInfos', ['putStaticInfoOutput']),
        slugUrl: function()
        {
            if(this.baseStaticInfo)
            {
                if(this.baseStaticInfo.slug)
                {
                    return this.baseStaticInfo.slug;
                }
                if(this.baseStaticInfo.slugs && this.baseStaticInfo.slugs.pt)
                {
                    return this.baseStaticInfo.slugs.pt;
                }
            }
            return null;
        },
        fullSlugUrl: function()
        {
            var baseUrl = process.env.VUE_APP_WEBSITE_URL || 'https://www.cfnumao.com';
            return this.slugUrl ? baseUrl + '/' + this.slugUrl : null;
        },
        title: function()
        {
            return this.baseStaticInfo || this.baseStaticInfoTitle ? (this.baseStaticInfo ? this.baseStaticInfo.title : this.baseStaticInfoTitle) : null;
        },
    },
    methods:{
        resetStaticInfo(){
            if(!this.baseStaticInfo)
            {
                var names = {};
                CONSTANTS.supportedLanguages.forEach(language => names[language.key] = "");

                this.staticInfo = new StaticInfo(names, [], null, []);
            }
            else
            {
                this.staticInfo = deepClone(new StaticInfo, this.baseStaticInfo);
                this.staticInfo.image = this.staticInfo.image ? {url: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, this.staticInfo.image])} : null;
                this.staticInfo.documents = (this.staticInfo.documents || []).map(doc => ({
                    ...doc,
                    previewUrl: doc.filePath ? API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, doc.filePath]) : null
                })); 
            }
        },
        submitClicked(){
            this.triedSubmit = true;
            // Emit event
            if(this.staticInfo.isValid())
            {
                this.submitting = true;
                this.$emit('submit-requested', this.staticInfo);
            }
        },
        genericValidator,
        errorValidator,
        onClickConfirmationPopup(value){
            this.showConfirmationPopup = value;
            router.push({name: 'StaticInfosList'});
        },
        addTab()
        {
            var titles = {};
            var contents = {};

            CONSTANTS.supportedLanguages.forEach(language => { titles[language.key] = ""; contents[language.key] = "" });

            this.staticInfo.tabs.push(new StaticInfoTab(titles, contents));
        },
        removeCurrentTab()
        {
            this.staticInfo.tabs.splice(this.currentTab, 1);
        },
        tabChanged(val)
        {
            this.currentTab = val;
        },
        addDocument()
        {
            this.$refs.documentInput.click();
        },
        onDocumentSelected(event)
        {
            const file = event.target.files[0];
            if (!file) return;

            if (file.size > 1024 * 1024 * MAX_DOCUMENT_SIZE)
            {
                event.target.value = '';
                alert(this.$t('fileErrorMessage', { size: MAX_DOCUMENT_SIZE }));
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                const base64 = e.target.result.split(',')[1];
                const mime = file.type;

                var titles = {};
                CONSTANTS.supportedLanguages.forEach(language => { titles[language.key] = "" });

                const byteCharacters = atob(base64);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
                const previewUrl = URL.createObjectURL(blob);
                this.blobUrls.push(previewUrl);

                this.staticInfo.documents.push({
                    id: null,
                    titles: titles,
                    fileName: file.name,
                    originalFileName: file.name,
                    file: { data: base64, mime: mime },
                    previewUrl: previewUrl
                });
            };
            reader.readAsDataURL(file);

            // Reset input so same file can be selected again
            event.target.value = '';
        },
        removeDocument(index)
        {
            this.staticInfo.documents.splice(index, 1);
        },
        onConfirmAddToMenu()
        {
            this.showChoicePopup = false;
            var query = {};

            // Try to extract slug from API response
            var output = this.postStaticInfoOutput;
            if (output && output.data) {
                if (output.data.slug) {
                    query.slug = output.data.slug;
                } else if (output.data.slugs && output.data.slugs.pt) {
                    query.slug = output.data.slugs.pt;
                }
            }

            // Use form titles as labels
            if (this.staticInfo.titles.pt) query.labelPt = this.staticInfo.titles.pt;
            if (this.staticInfo.titles.en) query.labelEn = this.staticInfo.titles.en;

            router.push({ name: 'MenuItemsAdd', query: query });
        },
        onCancelAddToMenu()
        {
            this.showChoicePopup = false;
            router.push({ name: 'StaticInfosList' });
        },
    },
    beforeDestroy()
    {
        this.blobUrls.forEach(url => URL.revokeObjectURL(url));
    },
    watch:
    {
        postStaticInfoOutput: function(val)
        {
            if(val)
            {
                this.showChoicePopup = true;
            }
        },
        putStaticInfoOutput: function(val)
        {
            if(val)
            {
                this.showConfirmationPopup = true;
            }
        },
        baseStaticInfo: function()
        {
            this.resetStaticInfo();
        },
        alertType: function()
        {
            this.submitting = false;
        },
        alertOutput: function()
        {
            this.submitting = false;

            // Show pop up in case it is a string error
            // In case it is an array, it is shown on the field
            if(typeof this.alertOutput === 'string' || this.alertOutput instanceof String)
            {
                alert(this.alertOutput);
            }
        }
    }
  }
</script> 
 
<style scoped>

    .gallery-picker
    {
        margin: 2rem auto;
    }

    .btn-add-tab, .btn-remove-tab
    {
        background-color: var(--primary);
        color: var(--secondary);
    }
</style> 